import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';
import FaqSchema from '../components/FaqSchema';
import RaadsRScreening from './RaadsRScreening';
import ReadMoreAutismSection from '../components/ReadMoreAutismSection';
import { 
  ChevronDown, 
  ChevronUp, 
  Check, 
  ArrowRight, 
  Users, 
  MessagesSquare, 
  Ear, 
  CalendarDays, 
  Target, 
  Layers, 
  Info, 
  FileSearch, 
  History, 
  SearchCheck, 
  FileCheck, 
  RefreshCw 
} from 'lucide-react';

export default function Autisme() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsPage, setCmsPage] = useState(null);
  const [cmsFaqs, setCmsFaqs] = useState([]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch('/_api/public/content/autisme_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching autisme_side:', err);
      }
    };

    const fetchFaqs = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:autisme&limit=7&sort=sortering&order=asc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsFaqs(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching Autisme FAQs:', err);
      }
    };

    fetchPage();
    fetchFaqs();
  }, [lang]);

  const seoTitle = lang === 'da' 
    ? 'Autisme-udredning og rådgivning | Nordisk Psykiatri' 
    : 'Autism Assessment and Guidance | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Specialiseret udredning og rådgivning for autisme hos unge og voksne. Få klarhed over dine mønstre, behov og styrker.'
    : 'Specialized assessment and guidance for autism in adolescents and adults. Gain clarity on your patterns, needs, and strengths.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqItems = [
    {
      q: 'Hvor lang tid tager en autisme-udredning?',
      a: 'En autismeudredning varer typisk 3-4 uger fra første samtale til den afsluttende tilbagemelding og epikrise.'
    },
    {
      q: 'Kan man opdage autisme som voksen?',
      a: 'Ja, mange voksne – især kvinder – opdager først autisme i voksenlivet, fordi de har brugt enormt meget energi på at tilpasse sig og maskere deres vanskeligheder.'
    },
    {
      q: 'Er autisme arveligt?',
      a: 'Ja, forskning viser, at autisme i høj grad er arveligt og genetisk betinget. Det er meget almindeligt, at der i samme familie findes flere med autistiske træk, autisme eller andre neurodivergente profiler såsom ADHD.'
    },
    {
      q: 'Hvad er Aspergers syndrom?',
      a: 'Aspergers syndrom er en tidligere diagnosebetegnelse inden for autismespektret, som blev anvendt om personer med autistiske træk uden generel sproglig eller kognitiv forsinkelse. I dag samles diagnoserne under den fælles betegnelse autismespektrumforstyrrelse (ASF).'
    },
    {
      q: 'Kan autisme forveksles med ADHD eller angst?',
      a: 'Ja, der er et betydeligt overlap i symptomer og oplevelser. Sanseovervældelse og social udmattelse kan ligne social angst eller belastningsreaktioner, mens udfordringer med overblik og eksekutive funktioner kan minde om ADHD. Mange har desuden både autisme og ADHD samtidigt (ofte kaldet AuDHD).'
    },
    {
      q: 'Ser autisme anderledes ud hos kvinder?',
      a: 'Ja, kvinder og piger med autisme fremstår ofte mere socialt velfungerende på overfladen, fordi mange tidligt tillærer sig strategier til at kompensere og "maskere" deres vanskeligheder. Det koster ofte enorme mængder energi og fører hyppigt til, at de først udredes i voksenlivet efter perioder med stress, udbrændthed eller depression.'
    },
    {
      q: 'Kan man udredes for både ADHD og autisme samtidig?',
      a: 'Ja, det er meget almindeligt, at der er overlap mellem ADHD og autisme. Vi kan tilrettelægge et kombineret udredningsforløb, så du ikke skal igennem to separate processer.'
    }
  ];

  const signs = [
    {
      icon: Users,
      title: '1. Socialt samspil',
      desc: 'Sociale situationer kan kræve analyse og meget energi.'
    },
    {
      icon: MessagesSquare,
      title: '2. Kommunikation',
      desc: 'Vanskeligheder med underforståede budskaber, ironi eller smalltalk.'
    },
    {
      icon: Ear,
      title: '3. Sansefølsomhed',
      desc: 'Lyde, lys, berøring eller mange indtryk kan opleves intenst.'
    },
    {
      icon: CalendarDays,
      title: '4. Behov for forudsigelighed',
      desc: 'Ændringer og uventede skift kan skabe uro og usikkerhed.'
    },
    {
      icon: Target,
      title: '5. Særlige interesser',
      desc: 'Dyb fordybelse og omfattende viden inden for bestemte områder.'
    },
    {
      icon: Layers,
      title: '6. Maskering og udmattelse',
      desc: 'Stor indsats for at tilpasse sig kan føre til overbelastning.'
    }
  ];

  const defaultConsiderPointsDa = [
    'Du har haft social usikkerhed hele livet.',
    'Du bliver let overvældet af sanseindtryk.',
    'Du har et stærkt behov for struktur og forudsigelighed.',
    'Du har intense interesser, der optager meget tid.',
    'Du bliver udmattet efter samvær med andre.',
    'Du oplever gentagne misforståelser i kommunikation.',
    'Du har gennem livet følt dig anderledes eller udenfor fællesskaber.'
  ];

  const defaultConsiderPointsSv = [
    'Du har haft social osäkerhet hela livet.',
    'Du blir lätt överväldigad av sinnesintryck.',
    'Du har ett starkt behov av struktur och förutsägbarhet.',
    'Du har intensiva intressen som tar mycket tid.',
    'Du blir utmattad efter samvaro med andra.',
    'Du upplever upprepade missförstånd i kommunikation.',
    'Du har genom livet känt dig annorlunda eller utanför gemenskapen.'
  ];

  const parseListItems = (rawHtml, defaultItems) => {
    if (!rawHtml || typeof rawHtml !== 'string') return defaultItems;
    const liMatches = [...rawHtml.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
      .map(m => m[1].replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);
    if (liMatches.length > 0) return liMatches;
    const pMatches = [...rawHtml.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)]
      .map(m => m[1].replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);
    if (pMatches.length > 0) return pMatches;
    const lines = rawHtml.split('\n').map(l => l.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
    return lines.length > 0 ? lines : defaultItems;
  };

  const considerPoints = cmsPage?.overveje_punkter
    ? parseListItems(getLocalizedValue(cmsPage.overveje_punkter), lang === 'da' ? defaultConsiderPointsDa : defaultConsiderPointsSv)
    : (lang === 'da' ? defaultConsiderPointsDa : defaultConsiderPointsSv);

  const stepIcons = [
    // Step 1: Speech / Chat Bubble
    <svg key="s1" className="w-7 h-7 md:w-8 md:h-8 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a.75.75 0 0 1-.974-.94 5.942 5.942 0 0 0 .917-2.613A8.75 8.75 0 0 1 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>,
    // Step 2: Clock / History (arrow pointing counter-clockwise / the other way)
    <History key="s2" className="w-7 h-7 md:w-8 md:h-8 stroke-[1.5]" aria-hidden="true" />,
    // Step 3: Search / Inspection
    <svg key="s3" className="w-7 h-7 md:w-8 md:h-8 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>,
    // Step 4: Document / Report
    <svg key="s4" className="w-7 h-7 md:w-8 md:h-8 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>,
    // Step 5: Repeat / Follow-up / Cycle
    <svg key="s5" className="w-7 h-7 md:w-8 md:h-8 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  ];

  const defaultSteps = [
    {
      title: lang === 'da' ? 'Indledende samtale og screening' : 'Initial consultation and screening',
      desc: lang === 'da'
        ? 'Vi taler om dine bekymringer, din baggrund og dit behov og vurderer sammen, om en udredning er relevant.'
        : 'We discuss your concerns, background, and needs, evaluating together whether an assessment is relevant.'
    },
    {
      title: lang === 'da' ? 'Udviklingshistorie og klinisk interview' : 'Developmental history and clinical interview',
      desc: lang === 'da'
        ? 'Din barndom, skolegang, relationer, arbejdsliv og nuværende hverdag gennemgås i detaljer.'
        : 'Your childhood, schooling, relationships, career, and current daily life are explored in detail.'
    },
    {
      title: lang === 'da' ? 'Diagnostisk undersøgelse' : 'Diagnostic assessment',
      desc: lang === 'da'
        ? 'Relevante kliniske interviews og validerede redskaber anvendes efter en faglig vurdering.'
        : 'Relevant clinical interviews and validated instruments are utilized based on clinical evaluation.'
    },
    {
      title: lang === 'da' ? 'Tilbagemelding og anbefalinger' : 'Feedback and recommendations',
      desc: lang === 'da'
        ? 'Resultaterne gennemgås, og du får konkrete anbefalinger til støtte, strategier og eventuelle næste skridt.'
        : 'Results are reviewed thoroughly, and you receive actionable recommendations for support and next steps.'
    },
    {
      title: lang === 'da' ? 'Opfølgning og behandling' : 'Follow-up and guidance',
      desc: lang === 'da'
        ? 'Der tilbydes samtaler, rådgivning og støtte efter behov.'
        : 'We provide consultations, guidance, and ongoing support tailored to your needs.'
    }
  ];

  const parsedStepsFromCms = useMemo(() => {
    if (!cmsPage?.proces_trin) return null;
    const html = getLocalizedValue(cmsPage.proces_trin);
    if (!html) return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const lis = Array.from(doc.querySelectorAll('li'));
      if (lis.length > 0) {
        return lis.map((li) => {
          const strong = li.querySelector('strong, b');
          let title = '';
          let desc = '';
          if (strong) {
            title = strong.textContent.trim().replace(/^0?\d+\.\s*/, '').replace(/:$/, '').trim();
            const clone = li.cloneNode(true);
            const sClone = clone.querySelector('strong, b');
            if (sClone) sClone.remove();
            desc = clone.textContent.trim().replace(/^:\s*/, '').trim();
          } else {
            const text = li.textContent.trim();
            const colonIdx = text.indexOf(':');
            if (colonIdx > 0 && colonIdx < 60) {
              title = text.slice(0, colonIdx).replace(/^0?\d+\.\s*/, '').trim();
              desc = text.slice(colonIdx + 1).trim();
            } else {
              title = text;
              desc = '';
            }
          }
          return { title, desc };
        });
      }
    } catch (e) {
      console.error('Error parsing proces_trin', e);
    }
    return null;
  }, [cmsPage?.proces_trin, lang, getLocalizedValue]);

  const activeSteps = (parsedStepsFromCms && parsedStepsFromCms.length > 0) ? parsedStepsFromCms : defaultSteps;

  const heroEyebrow = cmsPage ? getLocalizedValue(cmsPage.hero_badge) || (lang === 'da' ? 'AUTISMEUDREDNING' : 'AUTISM ASSESSMENT') : (lang === 'da' ? 'AUTISMEUDREDNING' : 'AUTISM ASSESSMENT');
  const heroTitle = cmsPage ? getLocalizedValue(cmsPage.hero_overskrift) || (lang === 'da' ? 'Autismeudredning for unge og voksne' : 'Autism Assessment and Guidance') : (lang === 'da' ? 'Autismeudredning for unge og voksne' : 'Autism Assessment and Guidance');
  const heroDesc = cmsPage ? getLocalizedValue(cmsPage.hero_beskrivelse) || (lang === 'da'
    ? 'Autisme kommer forskelligt til udtryk fra person til person, og både udfordringer, behov og styrker kan variere betydeligt. Derfor tager vores autismeudredning udgangspunkt i det enkelte menneske og det samlede billede – ikke kun i enkelte symptomer. Vi tilbyder grundig udredning af unge og voksne i København med en neuroaffirmativ tilgang og fokus på at skabe en nuanceret forståelse af den enkeltes måde at fungere på.'
    : 'Autism presents differently from person to person, and challenges, needs, and strengths can vary significantly. Therefore, our autism assessment is centered on the whole individual – not just isolated symptoms. We offer thorough assessments for adolescents and adults in Copenhagen with a neuro-affirmative approach and a focus on fostering nuanced understanding.') : (lang === 'da'
    ? 'Autisme kommer forskelligt til udtryk fra person til person, og både udfordringer, behov og styrker kan variere betydeligt. Derfor tager vores autismeudredning udgangspunkt i det enkelte menneske og det samlede billede – ikke kun i enkelte symptomer. Vi tilbyder grundig udredning af unge og voksne i København med en neuroaffirmativ tilgang og fokus på at skabe en nuanceret forståelse af den enkeltes måde at fungere på.'
    : 'Autism presents differently from person to person, and challenges, needs, and strengths can vary significantly. Therefore, our autism assessment is centered on the whole individual – not just isolated symptoms. We offer thorough assessments for adolescents and adults in Copenhagen with a neuro-affirmative approach and a focus on fostering nuanced understanding.');
  const DEFAULT_HERO_IMAGE = "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/autismeudredning-unge-voksne-koebenhavn_8e95db8e.jpg";
  const rawHeroImage = (cmsPage && cmsPage.hero_billede && cmsPage.hero_billede[0]) || DEFAULT_HERO_IMAGE;
  const heroImage = rawHeroImage && rawHeroImage.includes('assets.auxodev.dk') && !rawHeroImage.includes('/cdn-cgi/image/')
    ? rawHeroImage.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/')
    : rawHeroImage;
  const mobileHeroImage = (cmsPage && cmsPage.hero_billede_mobil && cmsPage.hero_billede_mobil[0])
    ? (cmsPage.hero_billede_mobil[0].includes('/cdn-cgi/image/') ? cmsPage.hero_billede_mobil[0] : `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${cmsPage.hero_billede_mobil[0].replace('https://assets.auxodev.dk/', '')}`)
    : "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-4-2026-11-26-34-am.png";
  const heroCtaText = cmsPage ? getLocalizedValue(cmsPage.hero_cta_tekst) || (lang === 'da' ? 'Kontakt os om autismeudredning' : 'Contact us regarding assessment') : (lang === 'da' ? 'Kontakt os om autismeudredning' : 'Contact us regarding assessment');
  const heroCtaLink = (cmsPage && cmsPage.hero_cta_link) || "/kontakt";

  const ctaTitle = cmsPage ? getLocalizedValue(cmsPage.cta_overskrift) || (lang === 'da' ? 'Klar til at tage det næste skridt?' : 'Ready to take the next step?') : (lang === 'da' ? 'Klar til at tage det næste skridt?' : 'Ready to take the next step?');
  const ctaDesc = cmsPage ? getLocalizedValue(cmsPage.cta_beskrivelse) || (lang === 'da' ? 'Book en uforpligtende forsamtale, og få overblik over mulighederne for en autisme-udredning.' : 'Book an initial consultation to get a clear overview of possibilities for an autism assessment.') : (lang === 'da' ? 'Book en uforpligtende forsamtale, og få overblik over mulighederne for en autisme-udredning.' : 'Book an initial consultation to get a clear overview of possibilities for an autism assessment.');
  const ctaBtn = cmsPage ? getLocalizedValue(cmsPage.cta_knap_tekst) || (lang === 'da' ? 'Book forsamtale' : 'Book initial consultation') : (lang === 'da' ? 'Book forsamtale' : 'Book initial consultation');
  const rawCtaImage = (cmsPage && cmsPage.cta_billede && cmsPage.cta_billede[0]) || null;
  const ctaImage = rawCtaImage && rawCtaImage.includes('assets.auxodev.dk') && !rawCtaImage.includes('/cdn-cgi/image/')
    ? rawCtaImage.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/')
    : rawCtaImage;

  // Parse custom related links from CMS if present
  const parseRelatedLinks = () => {
    const raw = cmsPage && getLocalizedValue(cmsPage.relaterede_links);
    if (!raw) return null;

    // Check if rich text contains <a> tags
    if (typeof raw === 'string' && raw.includes('<a')) {
      const links = [];
      const linkRegex = /<a[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
      let match;
      while ((match = linkRegex.exec(raw)) !== null) {
        const url = match[1];
        const text = match[2].replace(/<[^>]*>/g, '').trim();
        if (text && url) {
          links.push({ label: text, to: url });
        }
      }
      if (links.length > 0) return links;
    }

    // Check if plain text separated by line breaks or pipes
    if (typeof raw === 'string' && raw.trim()) {
      const clean = raw.replace(/<[^>]*>/g, '\n');
      const lines = clean.split('\n').map(l => l.trim()).filter(Boolean);
      const links = [];
      for (const line of lines) {
        if (line.includes('|')) {
          const [label, to] = line.split('|').map(s => s.trim());
          if (label && to) links.push({ label, to });
        } else if (line.includes('http') || line.startsWith('/')) {
          links.push({ label: line, to: line });
        }
      }
      if (links.length > 0) return links;
    }

    return null;
  };

  const dynamicRelatedLinks = parseRelatedLinks();

  const defaultRelatedLinks = [
    { label: 'ADHD', to: '/adhd' },
    { label: lang === 'da' ? 'Angst' : 'Anxiety', to: '/psykiatriske-lidelser/angst' },
    { label: 'Depression', to: '/psykiatriske-lidelser/depression' },
    { label: 'OCD', to: '/psykiatriske-lidelser/ocd' },
    { label: lang === 'da' ? 'Se priser' : 'See prices', to: '/priser' },
  ];

  const relatedLinksToRender = dynamicRelatedLinks || defaultRelatedLinks;

  // Sektion 1: Om Autisme
  const omAutismeBadge = cmsPage ? getLocalizedValue(cmsPage.om_autisme_badge) || (lang === 'da' ? 'OM AUTISME' : 'ABOUT AUTISM') : (lang === 'da' ? 'OM AUTISME' : 'ABOUT AUTISM');
  const omAutismeOverskrift = cmsPage ? getLocalizedValue(cmsPage.om_autisme_overskrift) || (lang === 'da' ? 'Hvad er autisme, og hvordan kommer det til udtryk?' : 'What is autism, and how does it present?') : (lang === 'da' ? 'Hvad er autisme, og hvordan kommer det til udtryk?' : 'What is autism, and how does it present?');
  const omAutismeBeskrivelse = cmsPage ? getLocalizedValue(cmsPage.om_autisme_beskrivelse) || (lang === 'da' ? 'Autisme er en medfødt neurobiologisk variation, som kan have betydning for socialt samspil, kommunikation, fleksibilitet og bearbejdning af sanseindtryk.' : 'Autism is an innate neurobiological difference that can affect social interaction, communication, flexibility, and sensory processing.') : (lang === 'da' ? 'Autisme er en medfødt neurobiologisk variation, som kan have betydning for socialt samspil, kommunikation, fleksibilitet og bearbejdning af sanseindtryk.' : 'Autism is an innate neurobiological difference that can affect social interaction, communication, flexibility, and sensory processing.');

  const omAutisme1Titel = cmsPage ? getLocalizedValue(cmsPage.om_autisme_1_titel) || (lang === 'da' ? 'Socialt samspil' : 'Social interaction') : (lang === 'da' ? 'Socialt samspil' : 'Social interaction');
  const omAutisme1Tekst = cmsPage ? getLocalizedValue(cmsPage.om_autisme_1_tekst) || (lang === 'da' ? 'Sociale situationer kan kræve analyse og meget energi.' : 'Social situations can require continuous analysis and immense energy.') : (lang === 'da' ? 'Sociale situationer kan kræve analyse og meget energi.' : 'Social situations can require continuous analysis and immense energy.');

  const omAutisme2Titel = cmsPage ? getLocalizedValue(cmsPage.om_autisme_2_titel) || (lang === 'da' ? 'Kommunikation' : 'Communication') : (lang === 'da' ? 'Kommunikation' : 'Communication');
  const omAutisme2Tekst = cmsPage ? getLocalizedValue(cmsPage.om_autisme_2_tekst) || (lang === 'da' ? 'Underforståede budskaber, ironi og smalltalk kan være vanskelige.' : 'Implicit cues, irony, and informal small talk can be challenging.') : (lang === 'da' ? 'Underforståede budskaber, ironi og smalltalk kan være vanskelige.' : 'Implicit cues, irony, and informal small talk can be challenging.');

  const omAutisme3Titel = cmsPage ? getLocalizedValue(cmsPage.om_autisme_3_titel) || (lang === 'da' ? 'Sansefølsomhed' : 'Sensory sensitivity') : (lang === 'da' ? 'Sansefølsomhed' : 'Sensory sensitivity');
  const omAutisme3Tekst = cmsPage ? getLocalizedValue(cmsPage.om_autisme_3_tekst) || (lang === 'da' ? 'Lyde, lys, berøring eller mange indtryk kan opleves intenst.' : 'Sounds, light, touch, or sensory overload can feel intense.') : (lang === 'da' ? 'Lyde, lys, berøring eller mange indtryk kan opleves intenst.' : 'Sounds, light, touch, or sensory overload can feel intense.');

  const omAutismeVariation = cmsPage ? getLocalizedValue(cmsPage.om_autisme_variation) || (lang === 'da' ? 'Udtryk og behov varierer meget fra person til person.' : 'Expressions and needs vary significantly from person to person.') : (lang === 'da' ? 'Udtryk og behov varierer meget fra person til person.' : 'Expressions and needs vary significantly from person to person.');

  const andreOmraaderOverskrift = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_overskrift) || (lang === 'da' ? 'Andre områder, der kan have betydning' : 'Other areas of significance') : (lang === 'da' ? 'Andre områder, der kan have betydning' : 'Other areas of significance');

  const andreOmraader1Titel = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_1_titel) || (lang === 'da' ? 'Behov for forudsigelighed' : 'Need for predictability') : (lang === 'da' ? 'Behov for forudsigelighed' : 'Need for predictability');
  const andreOmraader1Tekst = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_1_tekst) || (lang === 'da' ? 'Ændringer og uventede skift kan skabe uro.' : 'Changes and unexpected transitions can cause distress.') : (lang === 'da' ? 'Ændringer og uventede skift kan skabe uro.' : 'Changes and unexpected transitions can cause distress.');

  const andreOmraader2Titel = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_2_titel) || (lang === 'da' ? 'Særlige interesser' : 'Special interests') : (lang === 'da' ? 'Særlige interesser' : 'Special interests');
  const andreOmraader2Tekst = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_2_tekst) || (lang === 'da' ? 'Dyb fordybelse og omfattende viden inden for bestemte områder.' : 'Deep immersion and extensive knowledge within specific subjects.') : (lang === 'da' ? 'Dyb fordybelse og omfattende viden inden for bestemte områder.' : 'Deep immersion and extensive knowledge within specific subjects.');

  const andreOmraader3Titel = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_3_titel) || (lang === 'da' ? 'Maskering og udmattelse' : 'Masking and exhaustion') : (lang === 'da' ? 'Maskering og udmattelse' : 'Masking and exhaustion');
  const andreOmraader3Tekst = cmsPage ? getLocalizedValue(cmsPage.andre_omraader_3_tekst) || (lang === 'da' ? 'Stor indsats for at tilpasse sig kan føre til overbelastning.' : 'Intense effort to adapt and fit in can lead to burnout.') : (lang === 'da' ? 'Stor indsats for at tilpasse sig kan føre til overbelastning.' : 'Intense effort to adapt and fit in can lead to burnout.');

  // Sektion 2: Når autisme ikke er tydelig ved første øjekast
  const rawForskelligtBillede = (cmsPage && cmsPage.forskelligt_billede && cmsPage.forskelligt_billede[0]) || "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-7-2026-11-24-10-am.png";
  const forskelligtBillede = rawForskelligtBillede.includes('assets.auxodev.dk') && !rawForskelligtBillede.includes('/cdn-cgi/image/')
    ? rawForskelligtBillede.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/')
    : rawForskelligtBillede;

  const forskelligtOverskrift = cmsPage ? getLocalizedValue(cmsPage.forskelligt_overskrift) || (lang === 'da' ? 'Når autisme ikke er tydelig ved første øjekast' : 'When autism is not evident at first glance') : (lang === 'da' ? 'Når autisme ikke er tydelig ved første øjekast' : 'When autism is not evident at first glance');
  const forskelligtUndertekst = cmsPage ? getLocalizedValue(cmsPage.forskelligt_undertekst) || (lang === 'da' ? 'Nogle bliver identificeret tidligt, mens andre udvikler strategier til at tilpasse sig og først bliver udredt som voksne.' : 'Some are identified early, while others develop adaptive strategies and are only assessed as adults.') : (lang === 'da' ? 'Nogle bliver identificeret tidligt, mens andre udvikler strategier til at tilpasse sig og først bliver udredt som voksne.' : 'Some are identified early, while others develop adaptive strategies and are only assessed as adults.');

  const forskelligt1Titel = cmsPage ? getLocalizedValue(cmsPage.forskelligt_1_titel) || (lang === 'da' ? 'Sociale Strategier' : 'Social strategies') : (lang === 'da' ? 'Sociale Strategier' : 'Social strategies');
  const forskelligt1Tekst = cmsPage ? getLocalizedValue(cmsPage.forskelligt_1_tekst) || (lang === 'da' ? 'Har lært sociale regler bevidst.' : 'Have learned social rules consciously.') : (lang === 'da' ? 'Har lært sociale regler bevidst.' : 'Have learned social rules consciously.');

  const forskelligt2Titel = cmsPage ? getLocalizedValue(cmsPage.forskelligt_2_titel) || (lang === 'da' ? 'Restitution' : 'Recovery & Rest') : (lang === 'da' ? 'Restitution' : 'Recovery & Rest');
  const forskelligt2Tekst = cmsPage ? getLocalizedValue(cmsPage.forskelligt_2_tekst) || (lang === 'da' ? 'Har behov for restitution efter sociale situationer.' : 'Require recovery time after social situations.') : (lang === 'da' ? 'Har behov for restitution efter sociale situationer.' : 'Require recovery time after social situations.');

  const forskelligt3Titel = cmsPage ? getLocalizedValue(cmsPage.forskelligt_3_titel) || (lang === 'da' ? 'Energiforbrug' : 'Energy expenditure') : (lang === 'da' ? 'Energiforbrug' : 'Energy expenditure');
  const forskelligt3Tekst = cmsPage ? getLocalizedValue(cmsPage.forskelligt_3_tekst) || (lang === 'da' ? 'Virker velfungerende udadtil, men bruger meget energi.' : 'Appear high-functioning outwardly, but expend substantial energy.') : (lang === 'da' ? 'Virker velfungerende udadtil, men bruger meget energi.' : 'Appear high-functioning outwardly, but expend substantial energy.');

  const forskelligt4Titel = cmsPage ? getLocalizedValue(cmsPage.forskelligt_4_titel) || (lang === 'da' ? 'Overbelastning' : 'Overload & Burnout') : (lang === 'da' ? 'Overbelastning' : 'Overload & Burnout');
  const forskelligt4Tekst = cmsPage ? getLocalizedValue(cmsPage.forskelligt_4_tekst) || (lang === 'da' ? 'Oplever gentagne perioder med overbelastning.' : 'Experience recurring periods of sensory or emotional overload.') : (lang === 'da' ? 'Oplever gentagne perioder med overbelastning.' : 'Experience recurring periods of sensory or emotional overload.');

  const vidsteDuTitel = cmsPage ? getLocalizedValue(cmsPage.vidste_du_titel) || (lang === 'da' ? 'Maskering af autisme' : 'Masking in autism') : (lang === 'da' ? 'Maskering af autisme' : 'Masking in autism');
  const vidsteDuTekst = cmsPage ? getLocalizedValue(cmsPage.vidste_du_tekst) || (lang === 'da' ? 'Autisme kan være mindre synlig hos personer, der gennem mange år har lært at maskere eller kompensere for deres vanskeligheder. Det gælder blandt andet mange kvinder.' : 'Autism may be less noticeable in individuals who have spent years learning to mask or compensate for their difficulties. This is particularly common among women.') : (lang === 'da' ? 'Autisme kan være mindre synlig hos personer, der gennem mange år har lært at maskere eller kompensere for deres vanskeligheder. Det gælder blandt andet mange kvinder.' : 'Autism may be less noticeable in individuals who have spent years learning to mask or compensate for their difficulties. This is particularly common among women.');

  return (
    <div className="bg-sand-light min-h-screen text-text-dark">
      {/* E.1 HERO */}
      <FluidSubpageHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        description={heroDesc}
        ctaText={heroCtaText}
        ctaLink={heroCtaLink}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        tertiaryCtaText={lang === 'da' ? 'Autisme test' : 'Autism test'}
        tertiaryCtaLink="/vidensbank/autisme-screening"
        bgImageUrl={heroImage}
        bgImageAlt={lang === 'da' ? 'Autisme-udredning og rådgivning hos Nordisk Psykiatri' : 'Autism assessment and guidance at Nordisk Psykiatri'}
        showBgOverlay={false}
        mobileShowBgBelow={true}
        mobileBgImageUrl={mobileHeroImage}
        mobileBgImageAlt={lang === 'da' ? 'Autisme-udredning hos Nordisk Psykiatri' : 'Autism assessment at Nordisk Psykiatri'}
        reviewStamp={cmsPage?.fagligt_gennemgaaet ? getLocalizedValue(cmsPage.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, clinical psychologist | Updated September 2026')}
      />

      {/* E.2 AUTISME OVERVIEW SEKTION */}
      <section 
        className="relative overflow-hidden py-16 sm:py-20 lg:py-24 text-[#183f3c] bg-[#D1E1BE]"
      >
        <div className="mx-auto max-w-[1000px] px-6 sm:px-8">
          
          {/* TOP */}
          <div className="text-center">
            <div className="text-[11px] font-bold tracking-[0.23em] text-[#183f3c] uppercase mb-4 sm:mb-5">
              {omAutismeBadge}
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-[#183f3c] leading-[1.12] sm:leading-[1.08]">
              {omAutismeOverskrift}
            </h2>

            <p className="text-sm sm:text-base text-[#43534e] max-w-[620px] mx-auto mt-5 sm:mt-6 leading-relaxed">
              {omAutismeBeskrivelse}
            </p>
          </div>

          {/* TRE HOVEDOMRÅDER */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-16 mt-12 sm:mt-14">
            
            {/* Socialt samspil */}
            <div className="text-center flex flex-col items-center">
              <div className="w-[72px] h-[72px] mb-4 sm:mb-5 flex items-center justify-center text-[#1e3a35]">
                <svg viewBox="0 0 64 64" fill="none" className="w-[62px] h-[62px]" aria-hidden="true">
                  <circle cx="23" cy="18" r="7" stroke="currentColor" strokeWidth="1.8" />
                  <circle cx="42" cy="20" r="6" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M12 47V40C12 31 17 26 24 26C31 26 36 31 36 40V47" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M36 31C38 28 41 27 44 27C50 27 54 32 54 39V47" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-normal text-[#183f3c] mb-2">
                {omAutisme1Titel}
              </h3>
              <p className="text-xs sm:text-sm text-[#3f4c48] leading-relaxed">
                {omAutisme1Tekst}
              </p>
            </div>

            {/* Kommunikation */}
            <div className="text-center flex flex-col items-center">
              <div className="w-[72px] h-[72px] mb-4 sm:mb-5 flex items-center justify-center text-[#1e3a35]">
                <svg viewBox="0 0 64 64" fill="none" className="w-[62px] h-[62px]" aria-hidden="true">
                  <path d="M16 18C16 11 22 7 30 7C38 7 44 12 44 19C44 26 38 31 30 31H24L17 36L19 29C17 26 16 22 16 18Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <path d="M34 32C36 28 41 26 46 26C53 26 58 31 58 37C58 43 53 47 46 47H42L48 53L38 49C34 48 31 45 30 42" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-normal text-[#183f3c] mb-2">
                {omAutisme2Titel}
              </h3>
              <p className="text-xs sm:text-sm text-[#3f4c48] leading-relaxed">
                {omAutisme2Tekst}
              </p>
            </div>

            {/* Sansefølsomhed */}
            <div className="text-center flex flex-col items-center">
              <div className="w-[72px] h-[72px] mb-4 sm:mb-5 flex items-center justify-center text-[#1e3a35]">
                <svg viewBox="0 0 64 64" fill="none" className="w-[62px] h-[62px]" aria-hidden="true">
                  <path d="M4 32H13L17 24L22 41L28 13L35 47L41 19L47 39L52 29L57 32H61" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-normal text-[#183f3c] mb-2">
                {omAutisme3Titel}
              </h3>
              <p className="text-xs sm:text-sm text-[#3f4c48] leading-relaxed">
                {omAutisme3Tekst}
              </p>
            </div>

          </div>

          {/* ANDRE OMRÅDER BUND */}
          <div className="max-w-[720px] mx-auto mt-12 sm:mt-16">
            
            <div className="text-center">
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#183f3c] leading-tight">
                {andreOmraaderOverskrift}
              </h2>

              {/* Linje divider med vinkel-ikon */}
              <div className="grid grid-cols-[1fr_26px_1fr] items-center gap-2.5 my-4 sm:my-6 text-[#617a6e]">
                <span className="block h-[1px] bg-[#617a6e]" />
                <svg viewBox="0 0 24 12" fill="none" className="w-5 mx-auto" aria-hidden="true">
                  <path d="M3 9L12 2L21 9" stroke="currentColor" strokeWidth="1.4" />
                </svg>
                <span className="block h-[1px] bg-[#617a6e]" />
              </div>
            </div>

            {/* LISTE AF OMRÅDER */}
            <div className="flex flex-col gap-4 sm:gap-5">
              
              {/* Forudsigelighed */}
              <div className="grid grid-cols-[54px_1fr] sm:grid-cols-[68px_1fr] items-center gap-4 sm:gap-6">
                <div className="w-[54px] h-[54px] sm:w-[58px] sm:h-[58px] rounded-full bg-white/50 flex items-center justify-center text-[#1e3a35] shrink-0">
                  <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
                    <rect x="16" y="18" width="32" height="29" rx="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M16 27H48" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M23 14V22M41 14V22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M24 33H27M33 33H36M41 33H44M24 39H27M33 39H36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-normal text-[#183f3c] mb-0.5 sm:mb-1">
                    {andreOmraader1Titel}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#43504c] leading-relaxed m-0">
                    {andreOmraader1Tekst}
                  </p>
                </div>
              </div>

              {/* Særlige interesser */}
              <div className="grid grid-cols-[54px_1fr] sm:grid-cols-[68px_1fr] items-center gap-4 sm:gap-6">
                <div className="w-[54px] h-[54px] sm:w-[58px] sm:h-[58px] rounded-full bg-white/50 flex items-center justify-center text-[#1e3a35] shrink-0">
                  <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
                    <circle cx="32" cy="32" r="19" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="32" cy="32" r="12" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="32" cy="32" r="5" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-normal text-[#183f3c] mb-0.5 sm:mb-1">
                    {andreOmraader2Titel}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#43504c] leading-relaxed m-0">
                    {andreOmraader2Tekst}
                  </p>
                </div>
              </div>

              {/* Maskering og udmattelse */}
              <div className="grid grid-cols-[54px_1fr] sm:grid-cols-[68px_1fr] items-center gap-4 sm:gap-6">
                <div className="w-[54px] h-[54px] sm:w-[58px] sm:h-[58px] rounded-full bg-white/50 flex items-center justify-center text-[#1e3a35] shrink-0">
                  <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
                    <rect x="13" y="25" width="33" height="17" rx="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M46 31H50V36H46" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <path d="M17 29H42" stroke="currentColor" strokeWidth="1" opacity=".5" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-normal text-[#183f3c] mb-0.5 sm:mb-1">
                    {andreOmraader3Titel}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#43504c] leading-relaxed m-0">
                    {andreOmraader3Tekst}
                  </p>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* E.3 AUTISME KAN SE FORSKELLIGT UD / AUTISME HOS VOKSNE */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* CONTENT */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-stretch text-[#2B332E]">
            
            {/* LEFT COLUMN: Main Title & Subtitle */}
            <div className="lg:col-span-4 flex flex-col justify-start space-y-4 sm:space-y-6">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-normal leading-[1.14] text-[#0C2E24] tracking-tight mb-2 sm:mb-3">
                {forskelligtOverskrift}
              </h2>
              <p className="text-sm sm:text-base leading-relaxed text-[#56615B] max-w-sm m-0">
                {forskelligtUndertekst}
              </p>
            </div>

            {/* MIDDLE COLUMN: Key Structured Points */}
            <div className="lg:col-span-4 space-y-6 sm:space-y-7">
              <div>
                <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-[#0C2E24] mb-1.5 font-sans">
                  {forskelligt1Titel}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-[#2B332E] m-0 font-sans">
                  {forskelligt1Tekst}
                </p>
              </div>

              <div>
                <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-[#0C2E24] mb-1.5 font-sans">
                  {forskelligt2Titel}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-[#2B332E] m-0 font-sans">
                  {forskelligt2Tekst}
                </p>
              </div>

              <div>
                <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-[#0C2E24] mb-1.5 font-sans">
                  {forskelligt3Titel}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-[#2B332E] m-0 font-sans">
                  {forskelligt3Tekst}
                </p>
              </div>

              <div>
                <h3 className="text-xs sm:text-sm font-bold tracking-widest uppercase text-[#0C2E24] mb-1.5 font-sans">
                  {forskelligt4Titel}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-[#2B332E] m-0 font-sans">
                  {forskelligt4Tekst}
                </p>
              </div>
            </div>

            {/* RIGHT COLUMN: Sidebar Callout */}
            <div className="lg:col-span-4 h-full border-l border-[#1e3a35]/40 pl-6 sm:pl-8 lg:pl-10 space-y-4 sm:space-y-5 pt-4 lg:pt-0 flex flex-col justify-start">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-[#0C2E24] text-[#0C2E24] font-serif italic text-sm sm:text-base shrink-0 leading-none">
                  i
                </span>
                <h3 className="font-display text-2xl sm:text-3xl lg:text-[32px] font-normal leading-tight text-[#0C2E24] m-0">
                  {vidsteDuTitel}
                </h3>
              </div>
              <p className="text-xs sm:text-sm md:text-base leading-relaxed text-[#2B332E] m-0 font-sans">
                {vidsteDuTekst}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* E.4 HVORNÅR BØR MAN OVERVEJE EN UDREDNING? */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#faf8f3]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div 
            className="w-full bg-[#CFE0DB] rounded-none border-l-4 sm:border-l-[6px] border-l-[#8DB3AB] px-8 sm:px-14 md:px-20 lg:px-24 py-12 sm:py-16 shadow-none"
            style={{ backgroundColor: '#CFE0DB' }}
          >
            <div className="max-w-4xl lg:max-w-5xl mx-auto">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-petrol-dark text-center mb-6 sm:mb-8">
                {cmsPage ? getLocalizedValue(cmsPage.overveje_overskrift) || (lang === 'da' ? 'Hvornår bør du overveje en autisme-udredning?' : 'När bör du överväga en autismutredning?') : (lang === 'da' ? 'Hvornår bør du overveje en autisme-udredning?' : 'När bör du överväga en autismutredning?')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-3.5 sm:gap-y-4 mb-6 sm:mb-8 mx-auto max-w-3xl">
                {considerPoints.map((pt, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-petrol-dark/10 text-petrol-dark flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" aria-hidden="true" />
                    </div>
                    <span className="text-xs sm:text-sm text-petrol-dark leading-relaxed">{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E.5 UDREDNINGSFORLØBET (Process Stepper / Roadmap) */}
      <section className="bg-[#FAF9F6] py-20 px-6 font-sans border-t border-[#CBD5CB]/40">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16 md:mb-20 space-y-3">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#5E6D69]">
              {cmsPage ? getLocalizedValue(cmsPage.forloeb_badge) || (lang === 'da' ? 'Autismeudredning trin for trin' : 'Autism Assessment Step by Step') : (lang === 'da' ? 'Autismeudredning trin for trin' : 'Autism Assessment Step by Step')}
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[2.6rem] text-[#1C332E] font-normal tracking-tight">
              {cmsPage ? getLocalizedValue(cmsPage.proces_overskrift) || (lang === 'da' ? 'Sådan foregår en autismeudredning hos os' : 'How an autism assessment takes place with us') : (lang === 'da' ? 'Sådan foregår en autismeudredning hos os' : 'How an autism assessment takes place with us')}
            </h2>
          </div>

          {/* Timeline Stepper */}
          <div className="relative">
            {/* Connecting Rail Line */}
            <div 
              className="hidden md:block absolute top-9 left-[10%] right-[10%] h-px bg-[#CBD5CB] z-0" 
              aria-hidden="true"
            />

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-4 relative z-10">
              {activeSteps.map((st, idx) => {
                const iconNode = stepIcons[idx % stepIcons.length];
                const circleBg = (idx === 1 || idx === 3) ? 'bg-[#efebe3]' : 'bg-[#E5EBE5]';
                return (
                  <div key={idx} className="flex flex-col items-center text-center">
                    <div className={`w-[72px] h-[72px] rounded-full ${circleBg} flex items-center justify-center text-[#1C332E] shadow-none mb-6 shrink-0 transition-transform duration-300 hover:scale-105`}>
                      {iconNode}
                    </div>
                    <h3 className="font-display text-lg font-medium leading-snug mb-3 text-[#1C332E] text-center max-w-[200px]">
                      {st.title}
                    </h3>
                    <p className="text-xs md:text-[13px] text-[#5E6D69] leading-relaxed max-w-[195px] text-center font-sans font-normal">
                      {st.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* RAADS-R SCREENING SECTION - INTEGRATED DIRECTLY ON AUTISME PAGE */}
      <section className="pt-4 sm:pt-6 lg:pt-8 pb-16 lg:pb-24 bg-sand-light border-t border-sand-medium/40">
        <RaadsRScreening isEmbedded={true} />
      </section>

      {/* LÆS MERE OM AUTISME (Nordic Editorial Health 3-column card grid) */}
      <ReadMoreAutismSection />

      {/* FAQ SECTION */}
      <section className="pt-0 pb-16 lg:pb-24 bg-sand-light border-t border-sand-medium/40">
        <FaqSchema 
          items={(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
            q: getLocalizedValue(f.spoergsmaal),
            a: getLocalizedValue(f.svar)
          })) : faqItems).slice(0, 7)} 
        />
        <div className="mx-auto max-w-[900px] px-6">
          <div className="bg-[#faf8f4] rounded-lg p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-petrol-dark mb-6">
              {cmsPage ? getLocalizedValue(cmsPage.faq_overskrift) || (lang === 'da' ? 'Ofte stillede spørgsmål om autisme' : 'Vanliga frågor om autism') : (lang === 'da' ? 'Ofte stillede spørgsmål om autisme' : 'Vanliga frågor om autism')}
            </h2>

            <div className="space-y-3">
              {(cmsFaqs.length > 0 ? cmsFaqs.map(f => ({
                q: getLocalizedValue(f.spoergsmaal),
                a: getLocalizedValue(f.svar)
              })) : faqItems).slice(0, 7).map((item, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div 
                    key={idx} 
                    className={`border-t border-[#d8d5ce] last:border-b border-[#d8d5ce] transition-all duration-200 ${
                      isOpen ? 'py-3' : 'py-1'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      aria-expanded={isOpen}
                      className={`flex w-full items-center justify-between text-left font-sans text-sm font-bold text-petrol-dark focus:outline-none transition-all duration-200 cursor-pointer ${
                        isOpen ? 'py-2' : 'py-1.5'
                      }`}
                    >
                      <span>{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-petrol-medium shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-petrol-medium shrink-0" />
                      )}
                    </button>
                    {/* Bevares altid i DOM for Google og crawlere */}
                    <div 
                      className={`text-xs sm:text-sm text-[#586067] leading-relaxed ${
                        isOpen ? 'mt-2 pb-2 block' : 'hidden'
                      }`}
                      dangerouslySetInnerHTML={typeof item.a === 'string' && item.a.includes('<') ? { __html: item.a } : undefined}
                    >
                      {typeof item.a === 'string' && item.a.includes('<') ? null : item.a}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AUTISME OG RELATEREDE UDFORDRINGER (Design Reference) */}
      <section className="bg-white px-6 sm:px-8 py-16 lg:py-20 border-t border-sand-medium/60">
        <div className="max-w-[900px] mx-auto text-center">
          {/* Heading */}
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-petrol-dark tracking-tight leading-snug">
            {cmsPage && getLocalizedValue(cmsPage.relaterede_overskrift)
              ? getLocalizedValue(cmsPage.relaterede_overskrift)
              : (lang === 'da' ? 'Autisme og relaterede udfordringer' : 'Autism and related challenges')}
          </h2>

          {/* Body Description */}
          <p className="mt-3 sm:mt-4 font-sans text-sm sm:text-base text-text-muted leading-relaxed max-w-2xl mx-auto">
            {cmsPage && getLocalizedValue(cmsPage.relaterede_tekst)
              ? getLocalizedValue(cmsPage.relaterede_tekst)
              : (lang === 'da'
                  ? 'Autisme kan forekomme sammen med andre psykiatriske tilstande. Op mod halvdelen af voksne med autisme har også ADHD. Vi tilbyder også udredning for ADHD, angst, depression, OCD og andre relaterede problemstillinger.'
                  : 'Autism can co-occur with other psychiatric conditions. Up to half of adults with autism also have ADHD. We also offer assessment for ADHD, anxiety, depression, OCD, and other related challenges.')}
          </p>

          {/* Pill Group */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {relatedLinksToRender.map((item, idx) => {
              const isExternal = item.to.startsWith('http') || item.to.startsWith('//');
              if (isExternal) {
                return (
                  <a
                    key={idx}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <Link
                  key={idx}
                  to={item.to}
                  className="px-7 py-3 rounded-full border border-petrol-dark/40 text-petrol-dark text-[15px] font-medium transition-all duration-200 hover:border-petrol-dark hover:bg-petrol-dark/5 focus:outline-none focus:ring-2 focus:ring-petrol-dark/20 inline-flex items-center justify-center cursor-pointer"
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection 
        title={ctaTitle}
        desc={ctaDesc}
        buttonText={ctaBtn}
        image={ctaImage || undefined}
      />
    </div>
  );
}